import { motion } from "framer-motion";
import { History, Zap, Rocket, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const timelineItems = [
  {
    phase: "THEN",
    icon: History,
    title: "Where We Started",
    year: "2013",
    description: "Founded in the UK, expanding to Houston, Texas to support international students pursuing education in the USA. Built on trust, dedication, and a mission to transform lives through education.",
    highlights: ["UK Foundation", "Houston Expansion", "First Scholarships"],
    color: "secondary",
    link: "/heritage",
  },
  {
    phase: "NOW",
    icon: Zap,
    title: "Where We Are Today",
    year: "2025",
    description: "A global network spanning 50+ countries, partnering with 200+ institutions, and supporting over 10,000 alumni worldwide. Modern services, digital-first approach, premium support.",
    highlights: ["Global Network", "200+ Partners", "10K+ Alumni"],
    color: "primary",
    link: "/services",
  },
  {
    phase: "NEXT",
    icon: Rocket,
    title: "Where We're Going",
    year: "Future",
    description: "Expanding into new markets, leveraging AI for personalized guidance, building the next generation of global leaders through innovative education pathways.",
    highlights: ["AI Guidance", "New Markets", "Leadership Programs"],
    color: "primary",
    link: "/about",
  },
];

export const ThenNowNext = () => {
  return (
    <section
      id="journey"
      data-testid="then-now-next-section"
      className="py-24 md:py-32 bg-[#060B19] relative overflow-hidden"
    >
      {/* Background elements */}
      <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[200px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <span className="text-sm uppercase tracking-widest font-semibold text-primary mb-4 block">
            Our Journey
          </span>
          <h2
            className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mb-6"
            data-testid="journey-heading"
          >
            A Continuous Story of{" "}
            <span className="gradient-text">Growth & Impact</span>
          </h2>
          <p className="text-lg text-white/60 leading-relaxed">
            From our humble beginnings to a global network, discover how we've evolved
            while staying true to our mission of transforming lives through education.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Connecting Line */}
          <div className="absolute top-1/2 left-0 right-0 h-[2px] bg-gradient-to-r from-secondary via-primary to-primary hidden lg:block" />

          {/* Timeline Items */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {timelineItems.map((item, index) => (
              <motion.div
                key={item.phase}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                data-testid={`timeline-${item.phase.toLowerCase()}`}
                className="relative"
              >
                {/* Phase Badge */}
                <div className="flex justify-center mb-6">
                  <div className={`relative z-10 w-20 h-20 rounded-2xl bg-${item.color}/20 border border-${item.color}/30 flex items-center justify-center`}
                    style={{
                      backgroundColor: item.color === 'secondary' ? 'rgba(226, 199, 146, 0.2)' : 'rgba(0, 229, 255, 0.2)',
                      borderColor: item.color === 'secondary' ? 'rgba(226, 199, 146, 0.3)' : 'rgba(0, 229, 255, 0.3)',
                    }}
                  >
                    <item.icon 
                      className="w-10 h-10" 
                      style={{ color: item.color === 'secondary' ? '#E2C792' : '#00E5FF' }}
                    />
                  </div>
                </div>

                {/* Card */}
                <div className="glass-card rounded-2xl p-8 h-full card-hover">
                  {/* Phase Label */}
                  <div className="flex items-center justify-between mb-4">
                    <span 
                      className="text-xs font-bold tracking-[0.3em] uppercase"
                      style={{ color: item.color === 'secondary' ? '#E2C792' : '#00E5FF' }}
                    >
                      {item.phase}
                    </span>
                    <span className="text-white/40 text-sm font-mono">{item.year}</span>
                  </div>

                  {/* Title */}
                  <h3 className="font-heading text-2xl font-bold text-white mb-4">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="text-white/60 leading-relaxed mb-6">
                    {item.description}
                  </p>

                  {/* Highlights */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {item.highlights.map((highlight, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 text-xs rounded-full bg-white/5 border border-white/10 text-white/70"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>

                  {/* Link */}
                  <Link
                    to={item.link}
                    className="inline-flex items-center gap-2 text-sm font-medium transition-colors group"
                    style={{ color: item.color === 'secondary' ? '#E2C792' : '#00E5FF' }}
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-16"
        >
          <p className="text-white/50 mb-6">
            Curious about our roots? Explore our complete brand history.
          </p>
          <Link
            to="/heritage"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full glass-card hover:bg-white/10 transition-all group"
          >
            <History className="w-5 h-5 text-secondary" />
            <span className="text-white font-medium">Visit Our Heritage Archive</span>
            <ArrowRight className="w-4 h-4 text-white/50 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
